import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Copy, MousePointerClick, ShoppingCart, Clock, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

const statusBadge = (status) => {
  const map = {
    pending:   { label: 'Pending',    style: 'background:#fef08a;color:#854d0e' },
    available: { label: 'Tersedia',   style: 'background:#bbf7d0;color:#166534' },
    paid:      { label: 'Dibayar',    style: 'background:#bfdbfe;color:#1e40af' },
    cancelled: { label: 'Dibatalkan', style: 'background:#fecaca;color:#991b1b' },
    approved:  { label: 'Disetujui',  style: 'background:#bbf7d0;color:#166534' },
    rejected:  { label: 'Ditolak',    style: 'background:#fecaca;color:#991b1b' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ ...Object.fromEntries(s.style.split(';').map(x => x.split(':'))), borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
      {s.label}
    </span>
  );
};

const TABS = ['KATALOG', 'LINK SAYA', 'KOMISI', 'PENARIKAN'];

// ─── Link Modal ──────────────────────────────────────────────────────────────
const LinkModal = ({ link, onClose }) => {
  const url = `${window.location.origin}/go/${link.code}`;
  const copy = () => { navigator.clipboard.writeText(url); toast.success('Link disalin!'); };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
      <Card style={{ width: 480, maxWidth: '90vw' }}>
        <CardHeader><CardTitle>Link Promosi</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input readOnly value={url} />
            <Button variant="outline" onClick={copy}><Copy size={16} /></Button>
          </div>
          <Button className="w-full" onClick={onClose}>Tutup</Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const AffiliateDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [affiliateStatus, setAffiliateStatus] = useState(null); // null=loading, 'none', 'pending', 'active'
  const [affiliate, setAffiliate] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('KATALOG');

  // Registration form
  const [regForm, setRegForm] = useState({ bank_name: '', bank_account_number: '', bank_account_name: '' });
  const [regLoading, setRegLoading] = useState(false);
  const [regDone, setRegDone] = useState(false);

  // Tab data
  const [packages, setPackages] = useState([]);
  const [links, setLinks] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);

  // Withdrawal form
  const [wdForm, setWdForm] = useState({ amount: '', bank_name: '', bank_account_number: '', bank_account_name: '' });
  const [wdLoading, setWdLoading] = useState(false);

  // Promote
  const [promoteLoading, setPromoteLoading] = useState(null); // package_id
  const [linkModal, setLinkModal] = useState(null);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // ── Load affiliate status ───────────────────────────────────────────────────
  const loadAffiliate = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/affiliate/me`, { withCredentials: true });
      setAffiliate(res.data);
      setAffiliateStatus(res.data.status); // 'pending' | 'active'
    } catch (err) {
      if (err.response?.status === 404) {
        setAffiliateStatus('none');
      } else {
        toast.error('Gagal memuat data affiliate');
        if (process.env.NODE_ENV === 'development') console.error(err);
      }
    }
  }, []);

  // ── Load dashboard stats ────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/affiliate/dashboard`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    }
  }, []);

  useEffect(() => {
    if (user) loadAffiliate();
  }, [user, loadAffiliate]);

  useEffect(() => {
    if (affiliateStatus === 'active') loadStats();
  }, [affiliateStatus, loadStats]);

  // ── Load tab data ───────────────────────────────────────────────────────────
  const loadTab = useCallback(async (tab) => {
    setTabLoading(true);
    try {
      if (tab === 'KATALOG') {
        const res = await axios.get(`${API}/affiliate/packages`, { withCredentials: true });
        setPackages(res.data);
      } else if (tab === 'LINK SAYA') {
        const res = await axios.get(`${API}/affiliate/links`, { withCredentials: true });
        setLinks(res.data);
      } else if (tab === 'KOMISI') {
        const res = await axios.get(`${API}/affiliate/commissions`, { withCredentials: true });
        setCommissions(res.data);
      } else if (tab === 'PENARIKAN') {
        const res = await axios.get(`${API}/affiliate/withdrawals`, { withCredentials: true });
        setWithdrawals(res.data);
      }
    } catch (err) {
      toast.error('Gagal memuat data');
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setTabLoading(false);
    }
  }, []);

  useEffect(() => {
    if (affiliateStatus === 'active') loadTab(activeTab);
  }, [affiliateStatus, activeTab, loadTab]);

  // ── Register ────────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      await axios.post(`${API}/affiliate/register`, regForm, { withCredentials: true });
      setRegDone(true);
      toast.success('Pendaftaran berhasil dikirim');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mendaftar');
    } finally {
      setRegLoading(false);
    }
  };

  // ── Promote ─────────────────────────────────────────────────────────────────
  const handlePromote = async (pkg) => {
    setPromoteLoading(pkg.id);
    try {
      const res = await axios.post(`${API}/affiliate/links`, { package_id: pkg.id }, { withCredentials: true });
      setLinkModal(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat link');
    } finally {
      setPromoteLoading(null);
    }
  };

  // ── Withdrawal ──────────────────────────────────────────────────────────────
  const handleWithdraw = async (e) => {
    e.preventDefault();
    setWdLoading(true);
    try {
      await axios.post(`${API}/affiliate/withdrawals`, { ...wdForm, amount: Number(wdForm.amount) }, { withCredentials: true });
      toast.success('Permintaan penarikan dikirim');
      setWdForm({ amount: '', bank_name: '', bank_account_number: '', bank_account_name: '' });
      loadTab('PENARIKAN');
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengajukan penarikan');
    } finally {
      setWdLoading(false);
    }
  };

  // ── Guards ──────────────────────────────────────────────────────────────────
  if (!user) return null;

  if (affiliateStatus === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Affiliate</h1>
          <p className="text-gray-500 mt-1">Kelola program afiliasi Anda</p>
        </div>

        {/* ── Not an affiliate ── */}
        {affiliateStatus === 'none' && (
          <Card className="max-w-lg mx-auto">
            <CardHeader>
              <CardTitle>Daftar Jadi Affiliate</CardTitle>
            </CardHeader>
            <CardContent>
              {regDone ? (
                <div className="text-center py-6 text-amber-600 font-medium">
                  Pendaftaran sedang ditinjau admin. Kami akan menghubungi Anda.
                </div>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Nama Bank</Label>
                    <Input
                      id="bank_name"
                      placeholder="cth. BCA, Mandiri, BNI"
                      value={regForm.bank_name}
                      onChange={e => setRegForm(p => ({ ...p, bank_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_number">Nomor Rekening</Label>
                    <Input
                      id="bank_account_number"
                      placeholder="1234567890"
                      value={regForm.bank_account_number}
                      onChange={e => setRegForm(p => ({ ...p, bank_account_number: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_name">Nama Pemilik Rekening</Label>
                    <Input
                      id="bank_account_name"
                      placeholder="Sesuai buku tabungan"
                      value={regForm.bank_account_name}
                      onChange={e => setRegForm(p => ({ ...p, bank_account_name: e.target.value }))}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={regLoading}>
                    {regLoading && <Loader2 className="animate-spin mr-2" size={16} />}
                    Daftar Jadi Affiliate
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Pending ── */}
        {affiliateStatus === 'pending' && (
          <Card className="max-w-lg mx-auto">
            <CardContent className="py-10 text-center">
              <Clock size={40} className="mx-auto mb-4 text-amber-500" />
              <p className="text-lg font-semibold text-amber-700">Pendaftaran sedang ditinjau admin</p>
              <p className="text-gray-500 mt-2">Kami akan mengaktifkan akun Anda setelah verifikasi selesai.</p>
            </CardContent>
          </Card>
        )}

        {/* ── Active dashboard ── */}
        {affiliateStatus === 'active' && (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <MousePointerClick className="text-blue-500" size={24} />
                    <div>
                      <p className="text-sm text-gray-500">Total Klik</p>
                      <p className="text-2xl font-bold">{stats?.total_clicks ?? '—'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="text-green-500" size={24} />
                    <div>
                      <p className="text-sm text-gray-500">Total Order</p>
                      <p className="text-2xl font-bold">{stats?.total_orders ?? '—'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Clock className="text-amber-500" size={24} />
                    <div>
                      <p className="text-sm text-gray-500">Pending Komisi</p>
                      <p className="text-xl font-bold">{stats ? fmt(stats.pending_commission) : '—'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Wallet className="text-emerald-500" size={24} />
                    <div>
                      <p className="text-sm text-gray-500">Available Komisi</p>
                      <p className="text-xl font-bold">{stats ? fmt(stats.available_commission) : '—'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b">
              {TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {tabLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="animate-spin" size={28} />
              </div>
            )}

            {/* ── KATALOG ── */}
            {!tabLoading && activeTab === 'KATALOG' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages.length === 0 && <p className="text-gray-500 col-span-full text-center py-8">Tidak ada paket tersedia.</p>}
                {packages.map(pkg => (
                  <Card key={pkg.id}>
                    <CardHeader>
                      <CardTitle className="text-base">{pkg.package_name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-lg font-bold text-primary">{fmt(pkg.price)}</p>
                      <p className="text-sm text-gray-500">
                        Komisi:{' '}
                        {pkg.commission_type === 'percentage'
                          ? `${pkg.commission_value}%`
                          : fmt(pkg.commission_value)}
                      </p>
                      <Button
                        className="w-full"
                        size="sm"
                        disabled={promoteLoading === pkg.id}
                        onClick={() => handlePromote(pkg)}
                      >
                        {promoteLoading === pkg.id && <Loader2 className="animate-spin mr-2" size={14} />}
                        Promosikan
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* ── LINK SAYA ── */}
            {!tabLoading && activeTab === 'LINK SAYA' && (
              <Card>
                <CardContent className="pt-4 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Paket</TableHead>
                        <TableHead>Kode</TableHead>
                        <TableHead>Total Klik</TableHead>
                        <TableHead>Unique Klik</TableHead>
                        <TableHead>Komisi</TableHead>
                        <TableHead>URL</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Belum ada link.</TableCell></TableRow>
                      )}
                      {links.map(link => {
                        const url = `${window.location.origin}/go/${link.code}`;
                        return (
                          <TableRow key={link.id}>
                            <TableCell>{link.package_name ?? link.package_id}</TableCell>
                            <TableCell className="font-mono">{link.code}</TableCell>
                            <TableCell>{link.total_clicks ?? 0}</TableCell>
                            <TableCell>{link.unique_clicks ?? 0}</TableCell>
                            <TableCell>{fmt(link.total_commission)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 truncate max-w-[140px]">{url}</span>
                                <button
                                  aria-label="Salin link"
                                  onClick={() => { navigator.clipboard.writeText(url); toast.success('Link disalin!'); }}
                                  className="text-gray-400 hover:text-gray-700"
                                >
                                  <Copy size={14} />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* ── KOMISI ── */}
            {!tabLoading && activeTab === 'KOMISI' && (
              <Card>
                <CardContent className="pt-4 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Paket</TableHead>
                        <TableHead>Order ID</TableHead>
                        <TableHead>Jumlah</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tanggal</TableHead>
                        <TableHead>Tersedia</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissions.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Belum ada komisi.</TableCell></TableRow>
                      )}
                      {commissions.map(c => (
                        <TableRow key={c.id}>
                          <TableCell>{c.package_name ?? '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{String(c.order_id).slice(0, 8)}…</TableCell>
                          <TableCell>{fmt(c.amount)}</TableCell>
                          <TableCell>{statusBadge(c.status)}</TableCell>
                          <TableCell>{c.created_at ? new Date(c.created_at).toLocaleDateString('id-ID') : '—'}</TableCell>
                          <TableCell>{c.available_at ? new Date(c.available_at).toLocaleDateString('id-ID') : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* ── PENARIKAN ── */}
            {!tabLoading && activeTab === 'PENARIKAN' && (
              <div className="space-y-6">
                {/* Balance summary */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Pending', value: stats?.pending_commission, color: 'text-amber-600' },
                    { label: 'Tersedia', value: stats?.available_commission, color: 'text-emerald-600' },
                    { label: 'Sudah Dibayar', value: stats?.paid_commission, color: 'text-blue-600' },
                  ].map(({ label, value, color }) => (
                    <Card key={label}>
                      <CardContent className="pt-4">
                        <p className="text-sm text-gray-500">{label}</p>
                        <p className={`text-xl font-bold ${color}`}>{value !== undefined ? fmt(value) : '—'}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Withdrawal form */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Ajukan Penarikan</CardTitle></CardHeader>
                  <CardContent>
                    <form onSubmit={handleWithdraw} className="space-y-4 max-w-md">
                      <div className="space-y-2">
                        <Label htmlFor="wd_amount">Jumlah (Rp)</Label>
                        <Input
                          id="wd_amount"
                          type="number"
                          min="1"
                          placeholder="Nominal penarikan"
                          value={wdForm.amount}
                          onChange={e => setWdForm(p => ({ ...p, amount: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wd_bank_name">Nama Bank</Label>
                        <Input
                          id="wd_bank_name"
                          placeholder="cth. BCA"
                          value={wdForm.bank_name}
                          onChange={e => setWdForm(p => ({ ...p, bank_name: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wd_account_number">Nomor Rekening</Label>
                        <Input
                          id="wd_account_number"
                          placeholder="1234567890"
                          value={wdForm.bank_account_number}
                          onChange={e => setWdForm(p => ({ ...p, bank_account_number: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wd_account_name">Nama Pemilik Rekening</Label>
                        <Input
                          id="wd_account_name"
                          placeholder="Sesuai buku tabungan"
                          value={wdForm.bank_account_name}
                          onChange={e => setWdForm(p => ({ ...p, bank_account_name: e.target.value }))}
                          required
                        />
                      </div>
                      <Button type="submit" disabled={wdLoading}>
                        {wdLoading && <Loader2 className="animate-spin mr-2" size={16} />}
                        Ajukan Penarikan
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Withdrawal history */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Riwayat Penarikan</CardTitle></CardHeader>
                  <CardContent className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tanggal</TableHead>
                          <TableHead>Jumlah</TableHead>
                          <TableHead>Bank</TableHead>
                          <TableHead>Rekening</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {withdrawals.length === 0 && (
                          <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8">Belum ada penarikan.</TableCell></TableRow>
                        )}
                        {withdrawals.map(w => (
                          <TableRow key={w.id}>
                            <TableCell>{w.created_at ? new Date(w.created_at).toLocaleDateString('id-ID') : '—'}</TableCell>
                            <TableCell>{fmt(w.amount)}</TableCell>
                            <TableCell>{w.bank_name}</TableCell>
                            <TableCell>{w.bank_account_number}</TableCell>
                            <TableCell>{statusBadge(w.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      {/* Link modal */}
      {linkModal && <LinkModal link={linkModal} onClose={() => setLinkModal(null)} />}
    </div>
  );
};

export default AffiliateDashboard;
